import React from 'react';
import useResizeObserver from 'hooks/use-resize-observer';
import { useMemo } from 'react';
import {
  HorizontalGridLines,
  LineSeries,
  VerticalGridLines,
  XAxis,
  XYPlot,
  YAxis,
} from 'react-vis';
import {
  getColorForIdx,
  getLabelForIdx,
  getMarkerForIdx,
  LR_MARGIN_WITH_TICKS,
  TICK_LINE_AXIS_STYLE,
} from './timeseriesplot';
import { flatMap, uniq } from 'lodash';
import { extent } from 'd3-array';
import {
  StoredVerticalProfilePlotItem,
  VerticalProfilePlotItemReadings,
} from 'util/backendapi/types/Model';
import { StoredVerticalProfilePlotWithArea } from 'ducks/stored-plot/detail';
import { formatDatetimeForDisplay } from 'util/dates';
import { PageOrientations } from 'components/modules/exportpanel/exportpanelconstants';
import { LabelSeries } from './react-vis-hacks';

interface VerticalProfilePlotProps {
  storedPlot: StoredVerticalProfilePlotWithArea;
  storedVerticalProfilePlotItem: StoredVerticalProfilePlotItem;
  plotReadings: VerticalProfilePlotItemReadings;
  orientation: PageOrientations;
  plotsPerRow?: number;
  isSSR?: boolean;
  width?: number;
  height?: number;
}

const axisStyle = {
  line: {
    stroke: '#cfcfcf',
  },
};

const tickSize = {
  tickSizeOuter: 0,
  tickSizeInner: 0,
};

const GRID_LINES_STYLE = {
  stroke: '#efefef',
};

/** Calculate date range covering every item in the plot - used on the PDF version. */
export function computeVerticalProfilePlotDateRange(
  storedVerticalProfilePlot: StoredVerticalProfilePlotWithArea,
  plotItemReadings: VerticalProfilePlotItemReadings[]
) {
  const readingTimes = flatMap(plotItemReadings.map((r) => r.reading_times));

  const range = extent(
    readingTimes
      .map((r) => r.reading_datetime)
      .concat(
        storedVerticalProfilePlot.reading_times.map((r) => r.reading_datetime)
      )
  );
  return {
    minDatetime: range[0] ?? null,
    maxDatetime: range[1] ?? null,
  };
}

/** Calculate date range for individual vertical profile plot items */
export function computeVerticalProfilePlotItemDateRange(
  storedVerticalProfilePlot: StoredVerticalProfilePlotWithArea,
  plotReadings: VerticalProfilePlotItemReadings | null
) {
  const readingTimes = plotReadings?.reading_times ?? [];
  const range = extent(
    readingTimes
      .map((r) => r.reading_datetime)
      .concat(
        storedVerticalProfilePlot.reading_times.map((r) => r.reading_datetime)
      )
  );
  return {
    minDatetime: range[0] ?? null,
    maxDatetime: range[1] ?? null,
  };
}

export function VerticalProfilePlot(props: VerticalProfilePlotProps) {
  const {
    storedPlot,
    storedVerticalProfilePlotItem,
    plotReadings,
    isSSR,
    plotsPerRow,
    orientation,
  } = props;

  const [plotWrapperRef, plotWrapperWidth, plotWrapperHeight] =
    useResizeObserver<HTMLDivElement>({
      defaultWidth: 500,
      defaultHeight: 500,
    });

  const plotSize = useMemo(() => {
    const size: {
      height: number;
      width: number;
    } = {
      height: plotWrapperWidth,
      width: plotWrapperHeight,
    };

    if (props.width) {
      size.width = props.width;
    }

    if (props.height) {
      size.height = props.height;
    }
    return size;
  }, [plotWrapperHeight, plotWrapperWidth, props.width, props.height]);

  // If there is a unique vertical profile x/y axis title for all the instrument types of observation points on the plot
  // then display that, otherwise the axis title is blank.

  const xAxisTitle = useMemo(() => {
    const uniqueInstrumentTypeXAxisTitles = uniq(
      plotReadings.instrument_types.map(
        (i) => i.vertical_profile_plot_x_axis_title
      )
    );
    return uniqueInstrumentTypeXAxisTitles.length === 1
      ? uniqueInstrumentTypeXAxisTitles[0]
      : '';
  }, [plotReadings.instrument_types]);

  const yAxisTitle = useMemo(() => {
    const uniqueInstrumentTypeYAxisTitles = uniq(
      plotReadings.instrument_types.map(
        (i) => i.vertical_profile_plot_y_axis_title
      )
    );
    return uniqueInstrumentTypeYAxisTitles.length === 1
      ? uniqueInstrumentTypeYAxisTitles[0]
      : '';
  }, [plotReadings.instrument_types]);

  const { xDomain, yDomain } = useMemo(() => {
    const allReadings = flatMap(
      plotReadings.reading_times.map((rt) => rt.nearest_readings)
    );

    const [xMin, xMax] = extent(allReadings.map((r) => r.value));
    const [yMin, yMax] = extent(allReadings.map((r) => r.depth));

    const xDomain = [
      storedVerticalProfilePlotItem.x_axis_range_min !== null
        ? +storedVerticalProfilePlotItem.x_axis_range_min
        : xMin,
      storedVerticalProfilePlotItem.x_axis_range_max !== null
        ? +storedVerticalProfilePlotItem.x_axis_range_max
        : xMax,
    ];

    const yDomain = [
      storedVerticalProfilePlotItem.y_axis_range_min !== null
        ? +storedVerticalProfilePlotItem.y_axis_range_min
        : yMin,
      storedVerticalProfilePlotItem.y_axis_range_max !== null
        ? +storedVerticalProfilePlotItem.y_axis_range_max
        : yMax,
    ];

    return { xDomain, yDomain };
  }, [storedVerticalProfilePlotItem, plotReadings]);

  // Create a series of readings for each reading time. The x value is the reading value,
  // the y value is the depth.
  const dataSeries = useMemo(() => {
    let nextMarkerIdx = 0;

    return plotReadings.reading_times.map((rt) => {
      const readings = rt.nearest_readings.map((r) => ({
        x: r.value,
        y: r.depth,
      }));

      const marker = rt.show_markers ? getMarkerForIdx(nextMarkerIdx++) : null;

      const reading_markers = rt.show_markers
        ? readings.map((r) => ({ ...r, label: marker }))
        : [];

      return {
        reading_datetime: rt.reading_datetime,
        readings,
        marker,
        reading_markers,
      };
    });
  }, [plotReadings]);

  // Depth increases going down the Y axis
  const invertedYDomain = [yDomain[1], yDomain[0]];

  const plotWrapperClassName =
    orientation === PageOrientations.LANDSCAPE
      ? 'vertical-profile-plot-landscape-wrapper'
      : 'vertical-profile-plot-portrait-wrapper';

  const plotLegendOrientationClassName =
    orientation === PageOrientations.PORTRAIT
      ? isSSR && plotsPerRow && plotsPerRow > 1
        ? 'plot-legend-vertical-profile-portrait-ssr-squashed'
        : 'plot-legend-vertical-profile-portrait'
      : 'plot-legend-vertical-profile-landscape';

  return (
    <div className={plotWrapperClassName}>
      <div
        className="plot-area plot-area-vertical-profile"
        ref={plotWrapperRef}
      >
        <XYPlot
          xDomain={xDomain}
          yDomain={invertedYDomain}
          margin={{ top: 25, bottom: 50, left: 50, right: 50 }}
          {...plotSize}
        >
          <HorizontalGridLines style={GRID_LINES_STYLE} />
          <VerticalGridLines style={GRID_LINES_STYLE} />
          <XAxis
            orientation="top"
            className="xAxis"
            position="middle"
            hideTicks
            style={TICK_LINE_AXIS_STYLE}
          />
          <YAxis
            orientation="right"
            className="yAxis"
            hideTicks
            style={TICK_LINE_AXIS_STYLE}
          />
          <XAxis
            title={xAxisTitle}
            top={plotSize.height - 5}
            orientation="bottom"
            position="middle"
            hideLine
            hideTicks
          />
          {/* Left y-axis title */}
          <YAxis
            title={yAxisTitle}
            left={0}
            marginLeft={0}
            orientation="left"
            position="middle"
            hideLine
            hideTicks
          />
          {/* Right y-axis title */}
          <YAxis
            title={yAxisTitle}
            marginLeft={20 + LR_MARGIN_WITH_TICKS}
            orientation="right"
            position="middle"
            hideLine
            hideTicks
          />
          {/* Top x-axis ticks */}
          <XAxis
            className="xAxis"
            style={axisStyle}
            orientation="top"
            {...tickSize}
          />
          {/* Bottom x-axis ticks */}
          <XAxis
            className="xAxis"
            style={axisStyle}
            orientation="bottom"
            {...tickSize}
          />
          {/* Line for each reading time */}
          {dataSeries.map((series, idx) => (
            <LineSeries
              key={`line-${idx}`}
              animation={false}
              className="plot-readings-line"
              stroke={getColorForIdx(idx)}
              data={series.readings}
            />
          ))}
          <YAxis style={axisStyle} {...tickSize} className="yAxis" />
          <YAxis
            style={axisStyle}
            {...tickSize}
            orientation="right"
            className="yAxis"
          />
          {/* Markers for series with them enabled */}
          {dataSeries.map(
            (series, idx) =>
              series.marker && (
                <LabelSeries
                  key={`marker-${idx}`}
                  className="plot-markers"
                  labelAnchorX="middle"
                  labelAnchorY="central"
                  animation={false}
                  style={{ fill: getColorForIdx(idx) }}
                  data={series.reading_markers}
                />
              )
          )}
        </XYPlot>
      </div>
      <div
        className={`plot-legend plot-legend-vertical-profile ${plotLegendOrientationClassName}`}
      >
        <ol>
          {dataSeries.map((series, idx) => (
            <div key={idx}>
              <li>
                <span style={{ color: getColorForIdx(idx) }}>
                  {getLabelForIdx(idx)}
                </span>{' '}
                <span
                  className="plot-marker-legend"
                  style={{ color: getColorForIdx(idx) }}
                >
                  {series.marker}
                </span>{' '}
                {formatDatetimeForDisplay(
                  series.reading_datetime,
                  storedPlot.area.time_zone.name
                )}
              </li>
            </div>
          ))}
        </ol>
      </div>
    </div>
  );
}
